<!-- Panel "Bối cảnh làm việc" — module-rail.jsx :: TenantSwitcher (Carot ERP v2).
     Lớp phủ nhẹ toàn màn; hộp nổi neo cạnh rail ở mép trái (Q-061): cách rail 8, cách mép
     trên/dưới 10, rộng 960, không tràn mép phải. Mọi kích thước đọc biến shell trong SCSS. -->
<div class="ctx-backdrop" (click)="onBackdropClick()"></div>

<div
  class="ctx-panel"
  role="dialog"
  aria-modal="true"
  [attr.aria-label]="'Layout.WorkingContext' | translate"
>
  <!-- Đầu -->
  <div class="ctx-head">
    <div class="ctx-head-text">
      <h1 class="ctx-title">{{ 'Layout.WorkingContext' | translate }}</h1>
      <p class="ctx-subtitle">{{ 'Layout.ContextPanel.Subtitle' | translate }}</p>
    </div>
    <button
      type="button"
      class="ctx-close"
      [attr.aria-label]="'Action.Close' | translate"
      (click)="onCancel()"
    >
      <svg class="ctx-icon-16" aria-hidden="true" focusable="false">
        <use [attr.href]="icons.close"></use>
      </svg>
    </button>
  </div>

  <!-- Dải tóm tắt: chỉ mục có nguồn dữ liệu thật -->
  <div class="ctx-summary" data-testid="ctx-summary">
    <div class="ctx-summary-item">
      <div class="ctx-summary-label">{{ 'Layout.TenantSection' | translate }}</div>
      <div class="ctx-summary-value-row">
        <span class="ctx-summary-value">{{ tenantName(pendingTenant) || '—' }}</span>
        @if (pendingTenant?.plan; as plan) {
          <span class="ctx-plan">{{ plan }}</span>
        }
      </div>
    </div>
    <div class="ctx-summary-item">
      <div class="ctx-summary-label">{{ 'Layout.BusinessUnitSection' | translate }}</div>
      <div class="ctx-summary-value-row">
        <span class="ctx-summary-value">{{ buSummary }}</span>
      </div>
    </div>
    <div class="ctx-summary-item">
      <div class="ctx-summary-label">{{ 'Layout.FacilitySection' | translate }}</div>
      <div class="ctx-summary-value-row">
        <span class="ctx-summary-value">{{ facilitySummary }}</span>
      </div>
    </div>
    @if (reportingCurrencySummary; as ccy) {
      <div class="ctx-summary-item">
        <div class="ctx-summary-label">
          {{ 'Layout.ContextPanel.ReportingCurrency' | translate }}
        </div>
        <div class="ctx-summary-value-row">
          <span class="ctx-summary-value">{{ ccy }}</span>
        </div>
      </div>
    }
    @if (fiscalSummary; as fiscal) {
      <div class="ctx-summary-item" data-testid="ctx-fiscal" [attr.title]="fiscal.tooltip || null">
        <div class="ctx-summary-label">{{ 'Layout.ContextPanel.FiscalPeriod' | translate }}</div>
        <div class="ctx-summary-value-row">
          <span class="ctx-summary-value">{{ fiscal.label }}</span>
        </div>
      </div>
    }
  </div>

  <!-- Thân: ba cột -->
  <div class="ctx-body">
    <!-- Cột 1: Tenant -->
    <section class="ctx-col" data-testid="ctx-col-tenant">
      <div class="ctx-col-head">
        <span class="ctx-col-title">{{ 'Layout.TenantSection' | translate }}</span>
        <span class="ctx-col-count">{{ tenants.length }}</span>
      </div>
      @if (showTenantSearch) {
        <label class="ctx-search">
          <svg class="ctx-icon-14" aria-hidden="true" focusable="false">
            <use [attr.href]="icons.search"></use>
          </svg>
          <input
            type="text"
            [(ngModel)]="tenantSearchTerm"
            [placeholder]="'Layout.ContextPanel.SearchTenant' | translate"
          />
        </label>
      }
      <div class="ctx-list">
        @for (tenant of filteredTenants; track tenant.id) {
          <button
            type="button"
            class="ctx-item"
            [class.is-selected]="tenant.id === pendingTenantId"
            [attr.aria-pressed]="tenant.id === pendingTenantId"
            (click)="onTenantSelect(tenant.id!)"
          >
            <span class="ctx-avatar" [class.is-on]="tenant.id === pendingTenantId">
              {{ tenantInitials(tenant) }}
            </span>
            <span class="ctx-item-text">
              <span class="ctx-item-title">{{ tenantName(tenant) }}</span>
              <span class="ctx-item-sub">
                <span class="ctx-mono">{{ tenant.code }}</span>
                @if (tenant.plan) {
                  <span class="ctx-plan">{{ ' · ' + tenant.plan }}</span>
                }
              </span>
            </span>
            <span class="ctx-tick">
              @if (tenant.id === pendingTenantId) {
                <svg class="ctx-icon-15" aria-hidden="true" focusable="false">
                  <use [attr.href]="icons.check"></use>
                </svg>
              }
            </span>
          </button>
        }
        @if (filteredTenants.length === 0) {
          <div class="ctx-empty">
            {{
              (tenants.length
                ? 'Layout.ContextPanel.NoTenantMatch'
                : 'Layout.ContextPanel.NoTenant'
              ) | translate
            }}
          </div>
        }
      </div>
    </section>

    <!-- Cột 2: Đơn vị kinh doanh (cây) -->
    <section class="ctx-col" data-testid="ctx-col-bu">
      <div class="ctx-col-head">
        <span class="ctx-col-title" [title]="'Layout.ContextPanel.BuHint' | translate">
          {{ 'Layout.BusinessUnitSection' | translate }}
        </span>
        <span class="ctx-col-count">{{ buCountLabel }}</span>
        <span class="ctx-spacer"></span>
        <button type="button" class="ctx-text-btn" (click)="expandAll()">
          {{ 'Layout.ContextPanel.ExpandAll' | translate }}
        </button>
        <button type="button" class="ctx-text-btn" (click)="collapseAll()">
          {{ 'Layout.ContextPanel.CollapseAll' | translate }}
        </button>
      </div>
      <label class="ctx-search">
        <svg class="ctx-icon-14" aria-hidden="true" focusable="false">
          <use [attr.href]="icons.search"></use>
        </svg>
        <input
          type="text"
          [(ngModel)]="buSearchTerm"
          [placeholder]="'Layout.ContextPanel.SearchBu' | translate"
        />
      </label>
      <div class="ctx-list">
        @for (row of buRows; track row.id) {
          <div
            class="ctx-tree-row"
            data-testid="ctx-bu-row"
            [attr.data-bu-id]="row.id"
            [attr.data-state]="row.state"
            [style.padding-left.px]="row.depth * 18"
          >
            <button
              type="button"
              class="ctx-twisty"
              [class.is-leaf]="!row.hasChildren"
              [attr.aria-label]="
                (row.open ? 'Layout.ContextPanel.Collapse' : 'Layout.ContextPanel.Expand')
                  | translate
              "
              [attr.aria-expanded]="row.hasChildren ? row.open : null"
              [disabled]="!row.hasChildren"
              (click)="toggleExpand(row)"
            >
              @if (row.hasChildren) {
                <svg
                  class="ctx-icon-12"
                  [class.is-open]="row.open"
                  aria-hidden="true"
                  focusable="false"
                >
                  <use [attr.href]="icons.chevron"></use>
                </svg>
              }
            </button>
            <button
              type="button"
              class="ctx-item"
              role="checkbox"
              [attr.aria-checked]="
                row.state === 'on' ? 'true' : row.state === 'mixed' ? 'mixed' : 'false'
              "
              (click)="toggleBu(row.id)"
            >
              <span
                class="ctx-check"
                [class.is-on]="row.state === 'on'"
                [class.is-mixed]="row.state === 'mixed'"
              >
                @if (row.state === 'on') {
                  <svg class="ctx-icon-11" aria-hidden="true" focusable="false">
                    <use [attr.href]="icons.checkBold"></use>
                  </svg>
                } @else if (row.state === 'mixed') {
                  <span class="ctx-check-dash"></span>
                }
              </span>
              <span class="ctx-item-text">
                <span class="ctx-item-title" [class.is-match]="row.match">
                  {{ row.bu.displayName || row.bu.name }}
                </span>
                <span class="ctx-item-sub">
                  <span class="ctx-mono">{{ row.bu.code }}</span>
                  {{ buSubLine(row) }}
                </span>
              </span>
              @if (row.tag) {
                <span class="ctx-tag">{{ row.tag }}</span>
              }
            </button>
          </div>
        }
        @if (!pendingTenantId) {
          <div class="ctx-empty">{{ 'Layout.SelectTenantFirst' | translate }}</div>
        } @else if (buRows.length === 0) {
          <div class="ctx-empty">
            {{
              (buSearchTerm.trim()
                ? 'Layout.ContextPanel.NoUnitMatch'
                : 'Layout.ContextPanel.NoUnit'
              ) | translate
            }}
          </div>
        }
      </div>
    </section>

    <!-- Cột 3: Cơ sở / Kho -->
    <section class="ctx-col ctx-col--last" data-testid="ctx-col-facility">
      <div class="ctx-col-head">
        <span class="ctx-col-title">{{ 'Layout.FacilitySection' | translate }}</span>
        <span class="ctx-col-count">{{ facilitiesForScope.length }}</span>
        <span class="ctx-spacer"></span>
        <span class="ctx-col-hint">
          @if (pendingFacilityIds.size) {
            {{
              'Layout.ContextPanel.SelectedCount' | translate: { count: pendingFacilityIds.size }
            }}
          } @else {
            {{ 'Layout.ContextPanel.MultiSelect' | translate }}
          }
        </span>
        @if (pendingFacilityIds.size) {
          <button
            type="button"
            class="ctx-text-btn ctx-text-btn--underline"
            (click)="clearFacilities()"
          >
            {{ 'Layout.ContextPanel.ClearSelection' | translate }}
          </button>
        }
      </div>
      <label class="ctx-search">
        <svg class="ctx-icon-14" aria-hidden="true" focusable="false">
          <use [attr.href]="icons.search"></use>
        </svg>
        <input
          type="text"
          [(ngModel)]="facilitySearchTerm"
          [placeholder]="'Layout.ContextPanel.SearchFacility' | translate"
        />
      </label>
      <div class="ctx-list">
        @if (filteredFacilities.length > 0) {
          <button
            type="button"
            class="ctx-item"
            role="checkbox"
            data-testid="ctx-facility-all"
            [attr.aria-checked]="
              allVisibleFacilitiesState === 'on'
                ? 'true'
                : allVisibleFacilitiesState === 'mixed'
                  ? 'mixed'
                  : 'false'
            "
            (click)="toggleAllVisibleFacilities()"
          >
            <span
              class="ctx-check"
              [class.is-on]="allVisibleFacilitiesState === 'on'"
              [class.is-mixed]="allVisibleFacilitiesState === 'mixed'"
            >
              @if (allVisibleFacilitiesState === 'on') {
                <svg class="ctx-icon-11" aria-hidden="true" focusable="false">
                  <use [attr.href]="icons.checkBold"></use>
                </svg>
              } @else if (allVisibleFacilitiesState === 'mixed') {
                <span class="ctx-check-dash"></span>
              }
            </span>
            <span class="ctx-item-text">
              <span class="ctx-item-title">
                {{
                  (facilitySearchTerm.trim()
                    ? 'Layout.ContextPanel.SelectResults'
                    : 'Layout.ContextPanel.SelectAllFacilities'
                  ) | translate: { count: filteredFacilities.length }
                }}
              </span>
              @if (pendingFacilityIds.size === 0) {
                <span class="ctx-item-sub">
                  {{ 'Layout.ContextPanel.ViewingAllFacilities' | translate }}
                </span>
              }
            </span>
          </button>
        } @else {
          <div class="ctx-empty">
            {{
              (facilitiesForScope.length
                ? 'Layout.ContextPanel.NoFacilityMatch'
                : 'Layout.ContextPanel.NoFacilityInScope'
              ) | translate
            }}
          </div>
        }
        @for (group of facilityGroups; track group.bu.id) {
          <div class="ctx-group-head" [title]="group.fullPath">
            <span class="ctx-group-name">{{ group.label }}</span>
            <span>{{ group.selectedCount }}/{{ group.items.length }}</span>
          </div>
          @for (facility of group.items; track facility.id) {
            <button
              type="button"
              class="ctx-item"
              role="checkbox"
              data-testid="ctx-facility-row"
              [attr.data-facility-id]="facility.id"
              [attr.aria-checked]="isFacilityOn(facility)"
              (click)="toggleFacility(facility)"
            >
              <span class="ctx-check" [class.is-on]="isFacilityOn(facility)">
                @if (isFacilityOn(facility)) {
                  <svg class="ctx-icon-11" aria-hidden="true" focusable="false">
                    <use [attr.href]="icons.checkBold"></use>
                  </svg>
                }
              </span>
              <span class="ctx-item-text">
                <span class="ctx-item-title">{{ facility.displayName || facility.name }}</span>
                @if (facilityTypeLabel(facility); as typeLabel) {
                  <span class="ctx-item-sub">{{ typeLabel }}</span>
                }
              </span>
              <span class="ctx-code">{{ facility.code }}</span>
            </button>
          }
        }
      </div>
    </section>
  </div>

  <!-- Chân -->
  <div class="ctx-foot">
    <span class="ctx-foot-note" data-testid="ctx-narrowed-notice">{{ narrowedNotice }}</span>
    <ds-button variant="secondary" (click)="onCancel()">
      {{ 'Action.Cancel' | translate }}
    </ds-button>
    <ds-button variant="cta" [disabled]="!canApply" (click)="onApply()">
      {{ 'Layout.ContextPanel.Apply' | translate }}
    </ds-button>
  </div>
</div>
